import { useAuth } from '@dx-ui/framework-auth-provider';
import { sendReward } from '@dx-ui/framework-conductrics';
import { env } from '@dx-ui/framework-env';
import { logError } from '@dx-ui/framework-logger';
import { BannerNotification } from '@dx-ui/osc-banner-notification';
import { Dialog, DialogBaseProps } from '@dx-ui/osc-dialog-v2';
import {
  JoinForm,
  trackJoinModalView,
  windowJoinFormMetrics,
  type JoinAnalytics,
  type Props as JoinFormProps,
} from '@dx-ui/osc-join-form';
import { LoginForm, type LoginFormProps, type LoginFormResponse } from '@dx-ui/osc-login';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { useJoinModalBenefitsSection } from './hooks/use-join-modal-benefits-section';
import { JoinModalBenefitsSection } from './join-modal-benefits-section';
import { Heading, Text } from '@dx-ui/global-ui-prism';

export type JoinModalProps = {
  /** Optional callback to run after login submit. Overrides internal `auth.login()`*/
  onLoginAttempt?: LoginFormProps['onLoginAttempt'];
  analytics?: JoinModalAnalytics;
  onSuccessfulJoin?: () => void;
  onSuccessfulLogin?: () => void;
  /** Headline for the join form */
  headline?: JoinFormProps['headline'];
  language: JoinFormProps['language'];
  /** Enrollment OCode, defaults to OHWGW */
  oCode?: JoinFormProps['oCode'];
  /** Metrics override for the join form */
  metrics?: JoinFormProps['metrics'];
} & Omit<DialogBaseProps, 'title' | 'ariaLabel' | 'hasChildrenWithTitle' | 'children'>;

export type JoinModalAnalytics = JoinAnalytics & {
  onJoinModalView?: () => void;
};

export const JoinModal: React.FC<JoinModalProps> = ({
  isOpen: controlledIsOpen,
  onDismiss,
  onOpenChange,
  language,
  oCode = 'OHWGW',
  headline,
  analytics,
  onSuccessfulLogin,
  onSuccessfulJoin,
  metrics: metricsOverride,
  onLoginAttempt,
  ...rest
}) => {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isJoinModalOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;
  const [initialUsername, setInitialUsername] = useState('');
  const { login } = useAuth();
  const { t } = useTranslation('osc-join-modal-button');
  const { shouldShowJoinModalBenefitsSection } = useJoinModalBenefitsSection(isJoinModalOpen);
  const origin = new URL(env('OHW_BASE_URL') || 'https://www.hilton.com').origin;
  const metrics = { ...windowJoinFormMetrics, ...metricsOverride };
  const isLegacyJoinAnalyticsEnabled = analytics?.isLegacyJoinAnalyticsEnabled ?? true;

  useEffect(() => {
    if (isJoinModalOpen && !showLoginForm) {
      if (isLegacyJoinAnalyticsEnabled) {
        trackJoinModalView();
        return;
      }
      analytics?.onJoinModalView?.();
    }
  }, [analytics, isJoinModalOpen, showLoginForm, isLegacyJoinAnalyticsEnabled]);

  const handleOpenChange = (isOpen: boolean) => {
    setShowLoginForm(false);
    if (isControlled) {
      onOpenChange?.(isOpen);
      if (!isOpen) onDismiss?.();
    } else {
      setUncontrolledIsOpen(isOpen);
    }
  };

  const handleLogin = (response: LoginFormResponse) => {
    if (onLoginAttempt) {
      onLoginAttempt(response);
    } else {
      login(response).catch((error) => {
        logError('OSC_JOIN_MODAL', error, 'Error on auth.login() after successful join');
      });
    }
    sendReward('g-bgQSVS0Q4h');
    sendReward('g-IqzR2S0Ai5');
    onSuccessfulLogin?.();
    handleOpenChange(false);
  };

  return (
    <Dialog
      {...rest}
      onOpenChange={handleOpenChange}
      onDismiss={() => handleOpenChange(false)}
      isOpen={isJoinModalOpen}
      size={showLoginForm ? 'md' : '2xl'}
      title={showLoginForm ? t('signIn') : t('joinForFree')}
      surfaceType="base"
      stickyHeader={true}
    >
      {!showLoginForm ? (
        <div className="flex flex-col gap-6">
          {shouldShowJoinModalBenefitsSection ? <JoinModalBenefitsSection /> : null}
          <div className="min-w-0 flex-1">
            <JoinForm
              language={language}
              oCode={oCode}
              origin={origin}
              headline={
                headline ||
                (!shouldShowJoinModalBenefitsSection ? (
                  <Heading level={1} size="lg" className="mb-2 text-center">
                    {t('joinHiltonHonors')}
                  </Heading>
                ) : null)
              }
              onSuccess={(data) => {
                const profileId = data?.createGuest?.data?.hhonorsNumber;
                const username = window.atob(sessionStorage.getItem('username') || '');
                if (profileId) {
                  sendReward('g-XuedMeNnr5');
                  onSuccessfulJoin?.();
                  setInitialUsername(username || profileId || '');
                }

                if (data?.createGuest?.data?.accessToken) {
                  const loginResponse: LoginFormResponse = {
                    autoLoginData: {
                      guestId: data.createGuest?.data?.guestId,
                      hhonorsNumber: data.createGuest?.data?.hhonorsNumber || '',
                      accessToken: data.createGuest?.data?.accessToken || '',
                      autoLoginStatus: data.createGuest?.data?.autoLoginStatus || '',
                    },
                  };
                  handleLogin(loginResponse);
                } else {
                  setShowLoginForm(true);
                }
              }}
              onSignInClick={() => {
                setShowLoginForm(true);
              }}
              analytics={analytics}
              metrics={metrics}
              isInModal={true}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {initialUsername ? (
            <BannerNotification status="success">
              <Text>{t('welcomeMessage')}</Text>
            </BannerNotification>
          ) : null}
          <LoginForm
            loginFormConfig={{
              showCreatePasswordLink: false,
              showForgetInfoLink: true,
              showJoinLink: false,
              showSecureInfoLink: false,
            }}
            onLoginAttempt={handleLogin}
            sourceId="OHW"
            isInModal={true}
            initialUsername={initialUsername}
          />
        </div>
      )}
    </Dialog>
  );
};
